/**
 * Growable little-endian binary writer and a bounds-checked reader. The wire format is
 * hand-rolled per message (docs/05 "Protocol"); strings are u8-length ASCII.
 */
export class Writer {
  private buf: ArrayBuffer;
  private view: DataView;
  private o = 0;

  constructor(capacity = 256) {
    this.buf = new ArrayBuffer(capacity);
    this.view = new DataView(this.buf);
  }

  private need(n: number) {
    if (this.o + n <= this.buf.byteLength) return;
    let cap = this.buf.byteLength * 2;
    while (cap < this.o + n) cap *= 2;
    const next = new ArrayBuffer(cap);
    new Uint8Array(next).set(new Uint8Array(this.buf, 0, this.o));
    this.buf = next;
    this.view = new DataView(next);
  }

  u8(v: number) {
    this.need(1);
    this.view.setUint8(this.o, v);
    this.o += 1;
    return this;
  }
  i8(v: number) {
    this.need(1);
    this.view.setInt8(this.o, v);
    this.o += 1;
    return this;
  }
  u16(v: number) {
    this.need(2);
    this.view.setUint16(this.o, v, true);
    this.o += 2;
    return this;
  }
  i16(v: number) {
    this.need(2);
    this.view.setInt16(this.o, v, true);
    this.o += 2;
    return this;
  }
  u32(v: number) {
    this.need(4);
    this.view.setUint32(this.o, v >>> 0, true);
    this.o += 4;
    return this;
  }
  i32(v: number) {
    this.need(4);
    this.view.setInt32(this.o, v | 0, true);
    this.o += 4;
    return this;
  }
  f64(v: number) {
    this.need(8);
    this.view.setFloat64(this.o, v, true);
    this.o += 8;
    return this;
  }
  str(s: string) {
    const n = Math.min(255, s.length);
    this.u8(n);
    for (let i = 0; i < n; i++) this.u8(s.charCodeAt(i) & 0x7f);
    return this;
  }
  bytes(): Uint8Array<ArrayBuffer> {
    return new Uint8Array(this.buf, 0, this.o).slice();
  }
}

export class DecodeError extends Error {}

export class Reader {
  private view: DataView;
  private o = 0;

  constructor(bytes: Uint8Array) {
    this.view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  }

  private need(n: number) {
    if (this.o + n > this.view.byteLength) throw new DecodeError('truncated message');
  }

  get remaining() {
    return this.view.byteLength - this.o;
  }
  u8() {
    this.need(1);
    return this.view.getUint8(this.o++);
  }
  i8() {
    this.need(1);
    return this.view.getInt8(this.o++);
  }
  u16() {
    this.need(2);
    const v = this.view.getUint16(this.o, true);
    this.o += 2;
    return v;
  }
  i16() {
    this.need(2);
    const v = this.view.getInt16(this.o, true);
    this.o += 2;
    return v;
  }
  u32() {
    this.need(4);
    const v = this.view.getUint32(this.o, true);
    this.o += 4;
    return v;
  }
  i32() {
    this.need(4);
    const v = this.view.getInt32(this.o, true);
    this.o += 4;
    return v;
  }
  f64() {
    this.need(8);
    const v = this.view.getFloat64(this.o, true);
    this.o += 8;
    return v;
  }
  str() {
    const n = this.u8();
    this.need(n);
    let s = '';
    for (let i = 0; i < n; i++) s += String.fromCharCode(this.view.getUint8(this.o + i));
    this.o += n;
    return s;
  }
}
